import React, { lazy } from 'react';
import { ArrowRight, PhoneIcon } from 'lucide-react';
// Titles and links come from the shared category tree (data/categoryNav.ts) so
// the homepage can never contradict the header nav or the category report.
import { getNavGroup, getNavItem } from '../../data/categoryNav';
const link = (groupId: Parameters<typeof getNavItem>[0], label: string) => {
  const item = getNavItem(groupId, label);
  return {
    label: item.label,
    href: item.href
  };
};
const mailboxPosts = link('mailboxes', 'Mailbox Posts');
type MiniLink = {
  label: string;
  href: string;
};
type Card = {
  title: string;
  subtitle: string;
  cta: string;
  href: string;
  images?: string[];
  image?: string;
  alt?: string;
  helperCue?: string;
  miniLinks?: MiniLink[];
  isSupport?: boolean;
  phone?: string;
  imageFit?: 'cover' | 'contain';
};
const CARDS: Card[] = [{
  title: getNavGroup('mailboxes').fullLabel,
  subtitle: 'Curbside, wall mount, locking, column, multi mount, and complete mailbox and post systems.',
  cta: 'Shop mailboxes',
  href: getNavGroup('mailboxes').href,
  image: 'https://codex.websiteforge.net/MidAt-category-images-2026-08-11/category-mailboxes-keystone-ks19a.png?v=20260811-remake4',
  alt: 'Solar Illuminated Single Post Traditional Mount System, SKU BLS-BL, in a residential curbside setting.',
  imageFit: 'cover',
  miniLinks: [mailboxPosts, link('mailboxes', 'Multi Mount Mailbox Systems'), link('mailboxes', 'Mailbox Parts & Accessories')]
}, {
  title: mailboxPosts.label,
  subtitle: 'Single, double, decorative, and replacement mailbox posts, grouped under Mailboxes.',
  cta: 'Shop mailbox posts',
  href: mailboxPosts.href,
  image: 'https://codex.websiteforge.net/MidAt-category-images-2026-08-11/category-mailbox-posts-mayne-5805b.png?v=20260811-remake2',
  alt: 'Mayne Liberty Vinyl Mailbox Post Black, SKU 5805B, installed at a residential curb.',
  imageFit: 'cover'
}, {
  title: getNavGroup('commercial').fullLabel,
  subtitle: 'Cluster mailboxes, CBUs, 4C systems, parcel lockers, collection boxes, and commercial parts.',
  cta: 'Shop commercial',
  href: getNavGroup('commercial').href,
  image: 'https://codex.websiteforge.net/MidAt-category-images-2026-08-11/category-commercial-florence-1570-12.png?v=20260811-remake2',
  alt: 'Florence 1570-12 Type II CBU commercial mailbox unit in a community setting.',
  imageFit: 'cover'
}, {
  title: getNavGroup('street-signs').fullLabel,
  subtitle: 'Street signs, stop signs, yield signs, speed limit signs, parking signs, and traffic signage.',
  cta: 'Shop street signs',
  href: getNavGroup('street-signs').href,
  image: 'https://codex.websiteforge.net/MidAt-category-images-2026-08-11/category-street-signs-r1-r8.png?v=20260811-remake2',
  alt: 'R1-1 Stop Sign and R1-2 Yield Sign installed in a residential street setting.',
  imageFit: 'cover'
}, {
  title: getNavGroup('decorative-signage').fullLabel,
  subtitle: 'Decorative signage, sign frames, bases and hardware, and message boards.',
  cta: 'Shop decorative signage',
  href: getNavGroup('decorative-signage').href,
  image: 'https://codex.websiteforge.net/MidAt-category-images-2026-08-11/category-decorative-signage-charleston.png?v=20260811-remake2',
  alt: 'Classic Series dual helicopter mount decorative street sign, SKU MP410/WRB1/TRO/DBL/TSB3, in a landscaped residential setting.',
  imageFit: 'cover'
}, {
  title: getNavGroup('outdoor-accessories').fullLabel,
  subtitle: 'Address plaques and house numbers, illuminated address plaques, planters and window boxes, garden decor, country bells, patio coolers, and door chimes.',
  cta: 'Shop outdoor accessories',
  href: getNavGroup('outdoor-accessories').href,
  image: 'https://codex.websiteforge.net/MidAt-category-images-2026-08-11/category-outdoor-accessories-bell-planter.png?v=20260811-remake2',
  alt: 'Whitehall Large Country Bell SKU 00604 and Mayne Fairfield Tall Patio Planter Black SKU 5829-B in a garden porch setting.',
  imageFit: 'cover'
}, {
  title: getNavGroup('lighting').fullLabel,
  subtitle: 'Exterior home lighting, outdoor lamp posts, commercial lighting, and light blocks.',
  cta: 'Shop outdoor lighting',
  href: getNavGroup('lighting').href,
  image: 'https://codex.websiteforge.net/MidAt-category-images-2026-08-11/category-lighting-mayne-5835b.png?v=20260811-remake2',
  alt: 'Mayne Signature Lamp Post Black SKU 5835-B with Special Lite 16 inch acorn globe SKU 2810-PC1016CL-INCAN along a residential walkway.',
  imageFit: 'cover'
}, {
  title: 'Need Help Choosing?',
  subtitle: 'Not sure which mailbox, post, CBU, sign, or lighting product fits your project? The MidAtlantic team can help narrow the options.',
  cta: 'Get help choosing',
  href: '/ContactUs.html',
  isSupport: true,
  phone: '301-620-7585'
}];
function CardImage({
  card
}: {
  card: Card;
}) {
  if (card.images && card.images.length > 0) {
    return <a href={card.href} aria-label={card.title} className="product-image-stage h-44 grid grid-cols-2 grid-rows-2 gap-px bg-brand-border/40 border-b border-brand-border/50 overflow-hidden focus:outline-none focus:ring-2 focus:ring-brand-maroon" data-id="element-248" data-mp="3476hcmda6h13">
        {card.images.slice(0, 4).map((src, i) => <div key={i} className="bg-white flex items-center justify-center p-2 overflow-hidden" data-id="element-249" data-mp="2qc40532zh58z" data-mpi={i}>
            <img src={src} alt="" aria-hidden="true" loading="lazy" className="max-h-full max-w-full object-contain mix-blend-multiply transform group-hover:scale-105 transition-transform duration-500 ease-out" data-id="element-250" data-mp="2gfr8ear011gv" />
          </div>)}
      </a>;
  }
  const fitClass = card.imageFit === 'cover' ? 'object-cover' : 'object-contain';
  return <a href={card.href} className="product-image-stage aspect-[15/7] w-full flex items-center justify-center border-b border-brand-border/50 overflow-hidden focus:outline-none focus:ring-2 focus:ring-brand-maroon" data-id="element-251" data-mp="2459qdnozllfp">
      <img src={card.image} alt={card.alt || card.title} className={`h-full w-full ${fitClass} object-center transform group-hover:scale-105 transition-transform duration-500 ease-out`} data-id="element-252" data-mp="1u60xky5uycux" />
    </a>;
}
function SupportCard({
  card
}: {
  card: Card;
}) {
  return <div className="group flex flex-col rounded-xl border border-brand-maroon/30 bg-brand-goldWash shadow-sm hover:shadow-md hover:border-brand-maroon/60 hover:-translate-y-1 transition-all duration-300 overflow-hidden" data-id="element-253" data-mp="2rtjgh1t8gvcn">
      <div className="aspect-[15/7] w-full flex items-center justify-center bg-brand-maroon/5 border-b border-brand-maroon/20" data-id="element-254" data-mp="12gph7spoa185">
        <div aria-hidden="true" className="relative h-16 w-24" data-id="element-255" data-mp="16ijcflh3md0x">
          <div className="absolute left-1 top-3 h-10 w-14 rounded-xl border-2 border-brand-maroon bg-brand-goldWash" data-id="element-256" data-mp="yyry877v6rsc">
            <span className="absolute inset-0 flex items-center justify-center text-[25px] font-bold leading-none text-brand-maroon" data-id="element-257" data-mp="u06xm6opa63d">
              ?
            </span>
            <span className="absolute -bottom-1 left-3 h-3 w-3 rotate-45 border-b-2 border-r-2 border-brand-maroon bg-brand-goldWash" data-id="element-258" data-mp="18ulkiibdxznb" />
          </div>
          <div className="absolute right-1 top-6 h-10 w-14 rounded-xl border-2 border-brand-maroon/70 bg-brand-goldWash/80" data-id="element-259" data-mp="g9bxbis9oxbe">
            <span className="absolute -bottom-1 right-3 h-3 w-3 rotate-45 border-b-2 border-r-2 border-brand-maroon/70 bg-brand-goldWash/80" data-id="element-260" data-mp="bb0n36y28ksn" />
          </div>
        </div>
      </div>

      <div className="p-6 flex flex-col flex-grow" data-id="element-261" data-mp="l6lk0qo9uuvj">
        <a href={card.href} className="focus:outline-none focus:underline" data-id="element-262" data-mp="3tn6t07gwlnyn">
          <h3 className="text-xl font-serif font-bold text-brand-charcoal mb-2 group-hover:text-brand-maroon transition-colors" data-id="element-263" data-mp="3tl2hb3crjknt">
            {card.title}
          </h3>
        </a>

        <p className="text-brand-muted text-sm mb-4 flex-grow leading-relaxed" data-id="element-264" data-mp="336c0jgd3x3ji">
          {card.subtitle}
        </p>

        {card.phone && <a href={`tel:${card.phone}`} className="inline-flex items-center gap-2 text-brand-maroon font-bold text-base mb-4 hover:text-brand-maroonDark transition-colors focus:outline-none focus:underline" data-id="element-265" data-mp="3d36sthise7pc">
            <PhoneIcon size={16} data-id="element-266" data-mp="3m33pa5bf4auy" />
            {card.phone}
          </a>}

        <a href={card.href} className="inline-flex items-center justify-center bg-brand-maroon text-white font-semibold text-sm px-4 py-2.5 rounded-md hover:bg-brand-maroonDark transition-colors mt-auto focus:outline-none focus:ring-2 focus:ring-brand-gold" data-id="element-267" data-mp="3r1ofrd0vbg67">
          {card.cta}
          <ArrowRight size={16} className="ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-268" data-mp="8q1mbdowo96u" />
        </a>
      </div>
    </div>;
}
export function DecisionCards() {
  return <section id="shop-by-need" className="py-12 lg:py-16 bg-brand-background relative z-20 -mt-4 lg:-mt-8" data-id="element-269" data-mp="1y3ri5uwo9p3d">
      <div className="max-w-7xl mx-auto px-4" data-id="element-270" data-mp="1p565ggdvt4ai">
        <div className="text-center max-w-3xl mx-auto mb-10" data-id="element-271" data-mp="1p5z2gpn1gffg">
          <span className="text-brand-maroon font-bold tracking-wider uppercase text-xs mb-2 block flex items-center justify-center gap-2" data-id="element-272" data-mp="1k6mg0602xade">
            <span className="w-8 h-px bg-brand-gold" data-id="element-273" data-mp="2gvu3vko67vkh"></span>
            Shop by need
            <span className="w-8 h-px bg-brand-gold" data-id="element-274" data-mp="2cvtghaj3ga1a"></span>
          </span>
          <h2 className="text-3xl lg:text-4xl font-serif font-bold text-brand-charcoal mb-3" data-id="element-275" data-mp="283ibvudes02e">
            Start with the category you need
          </h2>
          <p className="text-lg text-brand-muted" data-id="element-276" data-mp="232uorjsaw3dg">
            Choose the catalog path that matches what you're shopping for.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-277" data-mp="2vtcnk8lzril2">
          {CARDS.map(card => card.isSupport ? <SupportCard key={card.title} card={card} data-id="element-278" data-mp="3h0gvpzxzoaln" /> : <div key={card.title} className="group flex flex-col bg-white rounded-xl border border-brand-border shadow-sm hover:shadow-md hover:border-brand-maroon/60 hover:-translate-y-1 transition-all duration-300 overflow-hidden" data-id="element-279" data-mp="3c1dpic69vyc3">
                <CardImage card={card} data-id="element-280" data-mp="3qsxgn7m4u85" />

                <div className="p-6 flex flex-col flex-grow" data-id="element-281" data-mp="3uyeys96v0klu">
                  <a href={card.href} className="focus:outline-none focus:underline" data-id="element-282" data-mp="3uxvp1frd52iw">
                    <h3 className="text-xl font-serif font-bold text-brand-charcoal mb-2 group-hover:text-brand-maroon transition-colors" data-id="element-283" data-mp="8j2m7zwzro8u">
                      {card.title}
                    </h3>
                  </a>

                  <p className="text-brand-muted text-sm mb-4 flex-grow leading-relaxed" data-id="element-284" data-mp="hka8fjm8gymk">
                    {card.subtitle}
                  </p>

                  {card.helperCue && <p className="text-brand-charcoal text-xs font-medium bg-brand-goldSoft p-3 rounded-md mb-4 border border-brand-gold/30" data-id="element-285" data-mp="164x3ku0ocavl">
                      {card.helperCue}
                    </p>}

                  {card.miniLinks && <div className="flex flex-wrap gap-2 mb-4" data-id="element-286" data-mp="w826mfk1uijf">
                      {card.miniLinks.map(link => <a key={link.label} href={link.href} className="text-xs bg-white hover:bg-brand-goldWash border border-brand-border text-brand-charcoal px-2.5 py-1 rounded-full transition-colors focus:outline-none focus:ring-1 focus:ring-brand-maroon" data-id="element-287" data-mp="n59g4eioyaun">
                          {link.label}
                        </a>)}
                    </div>}

                  <a href={card.href} className="inline-flex items-center text-brand-charcoal font-semibold text-sm hover:text-brand-maroon transition-colors mt-auto pt-4 border-t border-brand-border/30 focus:outline-none focus:underline" data-id="element-288" data-mp="263xm3afhuzea">
                    {card.cta}
                    <ArrowRight size={16} className="ml-2 transform group-hover:translate-x-1 transition-transform text-brand-maroon" data-id="element-289" data-mp="1sdu6v3f185yc" />
                  </a>
                </div>
              </div>)}
        </div>
      </div>
    </section>;
}